import React, { useState, useMemo } from 'react'
import { debounce } from 'lodash'

import PinService from 'services/pin.service'
import ToastService from 'services/toast.service'

import BasicCard from 'components/card/BasicCard'
import Icon from 'components/elements/Icon'
import CornerQuantity from 'components/collectionV2/views/Image/ImageCollectionCard/CornerQuantity'
import DraggableBasicCard from 'components/card/DraggableBasicCard'
import CornerOwned from 'components/card/controlsAndInfo/cornerOwned'
import Prices from 'components/card/controlsAndInfo/prices'

import { PinnedCardsRecordType } from 'services/apiTypes/pinnedCards.types'

import styles from './imagePinnedCard.module.scss'

type CardActions = {
  onCardUpdated: (updatedCard: PinnedCardsRecordType | PinnedCardsRecordType[], removedId?: number | number[]) => void
  cardClick: (e: React.MouseEvent, skipOpenOnClick?: boolean) => void
  openCardModal: (card: PinnedCardsRecordType) => void
}

type Props = {
  pinnedCard: PinnedCardsRecordType
  actions: CardActions
  setIsDragging: (isDragging: boolean) => void
}

const ImagePinnedCard = ({ pinnedCard, actions, setIsDragging }: Props) => {
  const [localQuantity, setLocalQuantity] = useState(pinnedCard.quantity)
  const [loading, setLoading] = useState(false)

  const isDeckPage = typeof window !== 'undefined' && location.pathname.startsWith('/decks/')

  const handleSetDebouncedQty = (quantity: number) => {
    const removeId = quantity < 1 ? pinnedCard.id : undefined
    const updatedCard = { ...pinnedCard, quantity: quantity < 1 ? 0 : quantity }

    const request = quantity < 1 ? PinService.remove : PinService.update

    setLoading(true)
    request(updatedCard)
      .then(() => {
        actions.onCardUpdated(updatedCard, removeId)
        ToastService.create(`${pinnedCard.card.name} updated`, 'Pinned Cards', 'success')
      })
      .catch(err => {
        if (localQuantity !== pinnedCard.quantity) setLocalQuantity(pinnedCard.quantity)
        ToastService.create(err.error, 'Pinned Cards', 'error')
      })
      .finally(() => setLoading(false))
  }

  const debouncedQtyChange = useMemo(() => {
    if (pinnedCard.quantity !== localQuantity) setLocalQuantity(pinnedCard.quantity)
    return debounce(handleSetDebouncedQty, 660)
  }, [pinnedCard])

  const handleIncrement = () => {
    setLocalQuantity(localQuantity + 1)
    debouncedQtyChange(localQuantity + 1)
  }

  const handleDecrement = () => {
    setLocalQuantity(localQuantity < 1 ? 0 : localQuantity - 1)
    debouncedQtyChange(localQuantity < 1 ? 0 : localQuantity - 1)
  }

  return (
    <div>
      <div className={styles.cardContainer}>
        {isDeckPage ? (
          <DraggableBasicCard
            card={{ ...pinnedCard.card, modifier: pinnedCard.modifier, deckRelationId: '' }}
            className={styles.basicCard}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={() => setIsDragging(false)}
            onDoubleClick={() => actions.openCardModal(pinnedCard)}
          />
        ) : (
          <BasicCard
            card={{ ...pinnedCard.card, modifier: pinnedCard.modifier }}
            className={styles.basicCard}
            imageClassName={styles.cardImage}
            onDoubleClick={() => actions.openCardModal(pinnedCard)}
          />
        )}

        {/* Bottom of the div so a z-index is unncessesary */}
        <CornerQuantity quantity={localQuantity} loading={loading} />
        <CornerOwned owned={pinnedCard.card.owned} />

        <div className={styles.quantityControls}>
          <button onClick={handleIncrement}>
            <Icon name="plus" />
          </button>

          <button onClick={handleDecrement}>
            <Icon name="minus" />
          </button>

          <button className={styles.cardDetailsButton} onClick={() => actions.openCardModal(pinnedCard)}>
            <Icon name="chevron right" />
          </button>
        </div>
      </div>

      <Prices card={pinnedCard.card} />
    </div>
  )
}

export default ImagePinnedCard
